import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { AUDIO_TRACKS } from "@/data/content";

const AudioContext = createContext(null);
export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);

  const current = AUDIO_TRACKS[index];

  const playIndex = useCallback((i) => {
    setIndex(i);
    setHasStarted(true);
    setIsPlaying(true);
  }, []);

  const playTrack = useCallback(
    (id) => {
      const i = AUDIO_TRACKS.findIndex((r) => r.id === id);
      if (i === -1) return;
      if (i === index && hasStarted) {
        setIsPlaying((p) => !p);
      } else {
        playIndex(i);
      }
    },
    [index, hasStarted, playIndex]
  );

  const togglePlay = useCallback(() => {
    if (!hasStarted) setHasStarted(true);
    setIsPlaying((p) => !p);
  }, [hasStarted]);

  const next = useCallback(() => playIndex((index + 1) % AUDIO_TRACKS.length), [index, playIndex]);
  const prev = useCallback(
    () => playIndex((index - 1 + AUDIO_TRACKS.length) % AUDIO_TRACKS.length),
    [index, playIndex]
  );

  const seek = useCallback((ratio) => {
    const a = audioRef.current;
    if (a && a.duration) {
      a.currentTime = ratio * a.duration;
      setProgress(ratio * a.duration);
    }
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) {
      const p = a.play();
      if (p && p.catch) p.catch(() => setIsPlaying(false));
    } else {
      a.pause();
    }
  }, [isPlaying, index]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const value = {
    current,
    index,
    isPlaying,
    hasStarted,
    progress,
    duration,
    volume,
    setVolume,
    playTrack,
    togglePlay,
    next,
    prev,
    seek,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        src={current.src}
        preload="metadata"
        onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
        onTimeUpdate={(e) => setProgress(e.target.currentTime || 0)}
        onEnded={next}
      />
    </AudioContext.Provider>
  );
};
